'use client';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './DropdownMenu';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { ReactNode } from 'react';

// Base menu item interface
interface BaseMenuItem {
  id: string;
  disabled?: boolean;
  className?: string;
}

// Regular menu item
interface RegularMenuItem extends BaseMenuItem {
  type: 'item';
  label: string;
  icon?: IconDefinition;
  shortcut?: string;
  variant?: 'default' | 'destructive';
  inset?: boolean;
  onClick?: () => void;
}

// Separator
interface SeparatorMenuItem extends BaseMenuItem {
  type: 'separator';
}

// Label
interface LabelMenuItem extends BaseMenuItem {
  type: 'label';
  label: string;
  inset?: boolean;
}

// Checkbox item
interface CheckboxMenuItem extends BaseMenuItem {
  type: 'checkbox';
  label: string;
  icon?: IconDefinition;
  shortcut?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

// Radio item (must be within a radio group)
interface RadioMenuItem extends BaseMenuItem {
  type: 'radio';
  label: string;
  icon?: IconDefinition;
  shortcut?: string;
  value: string;
}

// Radio group
interface RadioGroupMenuItem extends BaseMenuItem {
  type: 'radioGroup';
  value: string;
  onValueChange: (value: string) => void;
  items: RadioMenuItem[];
}

// Submenu
interface SubmenuMenuItem extends BaseMenuItem {
  type: 'submenu';
  label: string;
  icon?: IconDefinition;
  inset?: boolean;
  items: MenuItem[];
}

// Group (for organizing items)
interface GroupMenuItem extends BaseMenuItem {
  type: 'group';
  items: MenuItem[];
}

// Union type for all menu items
type MenuItem =
  | RegularMenuItem
  | SeparatorMenuItem
  | LabelMenuItem
  | CheckboxMenuItem
  | RadioGroupMenuItem
  | SubmenuMenuItem
  | GroupMenuItem;

// Configuration interface
interface DropdownMenuConfig {
  trigger: ReactNode;
  items: MenuItem[];
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  className?: string;
}

interface DropdownMenuGeneratorProps {
  config: DropdownMenuConfig;
}

// Helper function to render menu items recursively
function renderMenuItem(item: MenuItem): ReactNode {
  switch (item.type) {
    case 'item':
      return (
        <DropdownMenuItem
          className={item.className}
          disabled={item.disabled}
          inset={item.inset}
          key={item.id}
          onClick={(e) => {
            e.stopPropagation();
            item.onClick?.();
          }}
          variant={item.variant}
        >
          {item.icon && <FontAwesomeIcon icon={item.icon} />}
          {item.label}
          {item.shortcut && <DropdownMenuShortcut>{item.shortcut}</DropdownMenuShortcut>}
        </DropdownMenuItem>
      );

    case 'separator':
      return <DropdownMenuSeparator className={item.className} key={item.id} />;

    case 'label':
      return (
        <DropdownMenuLabel className={item.className} inset={item.inset} key={item.id}>
          {item.label}
        </DropdownMenuLabel>
      );

    case 'checkbox':
      return (
        <DropdownMenuCheckboxItem
          className={item.className}
          checked={item.checked}
          disabled={item.disabled}
          key={item.id}
          onCheckedChange={item.onCheckedChange}
        >
          {item.icon && <FontAwesomeIcon icon={item.icon} />}
          {item.label}
          {item.shortcut && <DropdownMenuShortcut>{item.shortcut}</DropdownMenuShortcut>}
        </DropdownMenuCheckboxItem>
      );

    case 'radioGroup':
      return (
        <DropdownMenuRadioGroup
          className={item.className}
          key={item.id}
          onValueChange={item.onValueChange}
          value={item.value}
        >
          {item.items.map((radioItem) => (
            <DropdownMenuRadioItem
              className={radioItem.className}
              disabled={radioItem.disabled}
              key={radioItem.id}
              value={radioItem.value}
            >
              {radioItem.icon && <FontAwesomeIcon icon={radioItem.icon} />}
              {radioItem.label}
              {radioItem.shortcut && <DropdownMenuShortcut>{radioItem.shortcut}</DropdownMenuShortcut>}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      );

    case 'submenu':
      return (
        <DropdownMenuSub key={item.id}>
          <DropdownMenuSubTrigger className={item.className} disabled={item.disabled} inset={item.inset}>
            {item.icon && <FontAwesomeIcon icon={item.icon} />}
            {item.label}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>{item.items.map(renderMenuItem)}</DropdownMenuSubContent>
        </DropdownMenuSub>
      );

    case 'group':
      return (
        <DropdownMenuGroup className={item.className} key={item.id}>
          {item.items.map(renderMenuItem)}
        </DropdownMenuGroup>
      );

    default:
      return null;
  }
}

export default function DropdownMenuGenerator({ config }: DropdownMenuGeneratorProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{config.trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        className={config.className}
        align={config.align}
        collisionPadding={12}
        side={config.side}
        sideOffset={config.sideOffset}
      >
        {config.items.map(renderMenuItem)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Export types for external use
export type {
  DropdownMenuConfig,
  MenuItem,
  RegularMenuItem,
  SeparatorMenuItem,
  LabelMenuItem,
  CheckboxMenuItem,
  RadioMenuItem,
  RadioGroupMenuItem,
  SubmenuMenuItem,
  GroupMenuItem,
};
